<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue'
import {useStore} from 'vuex'
import { MapSource, MapLayer } from '@/components/map'
import { HOST, API } from '@/utils/http'
import LayerCollectedDataPopup from './LayerCollectedDataPopup.vue'
import { useToast } from '@/components/ui/toast'

const { toast } = useToast()
const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const map = computed(() => store.getters['geo/getMap'])
const selectedRange = computed(() => store.state.layers.selectedRange)
const selectedFeature = computed(() => store.state.layers.selectedFeature)

const emit = defineEmits(['isLoaded', 'datesLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  layer: {
    type: Object,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  }
})

const isPopupVisible = ref(false)
const popupCoordinates = ref(null)
const popupFeature = ref(null)
const popupCounter = ref(0)
const isLoadingDates = ref(false)
const layerDates = ref([])

const storeLayer = computed(() => {
  const orgId = props.orgId
  return store.getters['layers/getOrgLayerById'](orgId, props.code)
})

const features = computed(() => {
  if (storeLayer.value?.properties?.features) {
    return storeLayer.value.properties.features;
  }
  return [];
})

const featureCollection = computed(() => {
  if (storeLayer.value?.properties?.featureCollection) {
    return storeLayer.value.properties.featureCollection;
  }
  return {
    type: 'FeatureCollection',
    features: features.value
  };
})

const isLoadingFeatures = ref(false)
const featuresCount = computed(() => features.value.length)

const sourceId = computed(() => `features-source-${props.code}`)
const pointLayerId = computed(() => `features-points-${props.code}`)
const lineLayerId = computed(() => `features-lines-${props.code}`)
const polygonLayerId = computed(() => `features-polygons-${props.code}`)
const polygonOutlineLayerId = computed(() => `features-polygon-outlines-${props.code}`)

const processDateFormat = (dateItem) => {
  if (!dateItem) return null;

  // Ensure the date property is present
  if (!dateItem.date && !dateItem.hour) return null;

  const dateStr = dateItem.date || dateItem.hour;

  // Create a standardized date object
  return {
    date: dateStr,
    count: dateItem.count || 1,
    label: dateItem.label || `${dateItem.count || 1} events`,
    vessels: dateItem.vessels || [],
    features: dateItem.features || []
  };
};

const fetchDates = async () => {
  try {
    isLoadingDates.value = true

    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/layers/${props.code}/radar/dates`
    })

    if (response.data && response.data.layers && Array.isArray(response.data.layers)) {
      const templateLayers = response.data.layers.find(layer => layer.id === 'features' && layer.layers);

      if (templateLayers && templateLayers.layers) {
        const processedLayers = templateLayers.layers.map(layer => {
          const processedDates = Array.isArray(layer.dates)
            ? layer.dates.map(processDateFormat)
            : [];

          return {
            id: layer.id || 'unknown',
            name: layer.name || 'Unknown Template',
            color: layer.color || 'blue',
            dates: processedDates
          };
        });

        layerDates.value = processedLayers
        emit('datesLoaded', processedLayers)

        if (processedLayers.length > 0) {
          try {
            store.commit('layers/ADD_DATES', processedLayers);
          } catch (storeError) {
            console.error('Error updating store with dates:', storeError)
          }
        }
      } else {
        const transformedLayers = response.data.layers.map(layer => {
          const processedDates = Array.isArray(layer.dates)
            ? layer.dates.map(processDateFormat)
            : [];

          return {
            id: layer.id || 'unknown',
            name: layer.name || (layer.id === 'features' ? 'Features' : layer.id),
            color: layer.color || 'blue',
            dates: processedDates
          };
        });

        layerDates.value = transformedLayers;
        emit('datesLoaded', transformedLayers);

        if (transformedLayers.length > 0) {
          try {
            store.commit('layers/ADD_DATES', transformedLayers);
          } catch (storeError) {
            console.error('Error updating store with dates:', storeError);
          }
        }
      }
    } else if (response.data && response.data.dates) {
      const dates = Array.isArray(response.data.dates) ? response.data.dates : [];
      const processedDates = dates.map(processDateFormat);

      const transformedData = {
        id: "features",
        name: "Features",
        color: "green",
        dates: processedDates
      };

      layerDates.value = [transformedData];
      emit('datesLoaded', [transformedData]);

      try {
        store.commit('layers/ADD_DATES', [transformedData]);
      } catch (storeError) {
        console.error('Error updating store with dates:', storeError);
      }
    }
  } catch (error) {
    console.error('Error fetching dates:', error)
    toast({
      title: 'Error loading dates',
      description: 'Failed to fetch dates for the layer',
      variant: 'destructive'
    })
  } finally {
    isLoadingDates.value = false
  }
}

const showPopup = (e) => {
  if (!e.features || e.features.length === 0) return

  const feature = e.features[0]
  popupFeature.value = feature
  popupCoordinates.value = e.lngLat.toArray()
  isPopupVisible.value = true
  popupCounter.value++
}

const closePopup = () => {
  isPopupVisible.value = false
  popupFeature.value = null
}

const setupLayerEvents = () => {
  if (!map.value) return

  const setupLayerClickEvent = (layerId) => {
    map.value.on('click', layerId, showPopup)
    map.value.on('mouseenter', layerId, () => {
      map.value.getCanvas().style.cursor = 'pointer'
    })

    map.value.on('mouseleave', layerId, () => {
      map.value.getCanvas().style.cursor = ''
    })
  }

  setupLayerClickEvent(pointLayerId.value)
  setupLayerClickEvent(lineLayerId.value)
  setupLayerClickEvent(polygonLayerId.value)
}

const fetchFeatures = async () => {
  isLoadingFeatures.value = true;

  try {
    const [startDate, endDate] = selectedRange.value;

    let url = `${HOST}/org/${props.orgId}/layers/${props.code}/features`;
    if (startDate && endDate) {
      url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }

    const response = await API({
      method: 'GET',
      url: url
    });

    if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      const orgId = props.orgId;
      const layerId = props.code;

      await store.dispatch('layers/updateOrgLayerFeatures', {
        orgId,
        layerId,
        features: response.data.features
      });

      emit('isLoaded', true);
    }
  } catch (error) {
    console.error('Error loading features:', error);
    toast({
      title: 'Error loading features',
      description: 'Failed to fetch features for the layer',
      variant: 'destructive'
    });
  } finally {
    isLoadingFeatures.value = false;
  }
}

watch(selectedFeature, (newSelectedFeature) => {
  if (newSelectedFeature?.feature && newSelectedFeature.isVisible &&
      newSelectedFeature.feature.properties?.code &&
      features.value.some(f => f.properties?.code === newSelectedFeature.feature.properties.code)) {

    popupFeature.value = newSelectedFeature.feature
    popupCoordinates.value = newSelectedFeature.coordinates
    isPopupVisible.value = true
    popupCounter.value++
  }
}, { deep: true })

let fetchFeaturesTimeout = null
const debouncedFetchFeatures = () => {
  if (fetchFeaturesTimeout) {
    clearTimeout(fetchFeaturesTimeout)
  }
  fetchFeaturesTimeout = setTimeout(() => {
    fetchFeatures()
  }, 500)
}

watch(selectedRange, () => {
  debouncedFetchFeatures()
})

watch(() => [map.value, featuresCount.value], ([newMap, newCount]) => {
  if (newMap && newCount > 0) {
    setTimeout(() => {
      setupLayerEvents();
    }, 200);
  }
}, { immediate: true });

// Watch for organization changes to refetch dates
watch(() => props.orgId, async (newOrgId, oldOrgId) => {
  if (newOrgId && newOrgId !== oldOrgId) {
    // Clear existing dates when switching organizations
    store.commit('layers/CLEAR_DATES');
    // Fetch new dates for the new organization
    await fetchDates();
    await fetchFeatures();
  }
});

onMounted(async () => {
  await fetchDates();
  await fetchFeatures();
})

onUnmounted(() => {
  // Clear dates when component is unmounted to prevent data leakage between organizations
  store.commit('layers/CLEAR_DATES');
})
</script>

<template>
  <MapSource
    v-if="featuresCount > 0 && map"
    :map="map"
    :id="sourceId"
    type="geojson"
    :data="featureCollection"
  >
    <MapLayer
      v-if="map"
      :map="map"
      :id="pointLayerId"
      :source="sourceId"
      type="circle"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 6,
          'circle-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.8
        }
      }"
    />

    <MapLayer
      v-if="map"
      :map="map"
      :id="lineLayerId"
      :source="sourceId"
      type="line"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-width': 3,
          'line-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'line-opacity': 0.8
        }
      }"
    />

    <!-- Polygon outline only (no fill) -->
    <MapLayer
      v-if="map"
      :map="map"
      :id="polygonOutlineLayerId"
      :source="sourceId"
      type="line"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#0059ff'],
          'line-width': 2,
          'line-opacity': 0.8
        }
      }"
    />

    <LayerCollectedDataPopup
      v-if="isPopupVisible && popupCoordinates"
      :map="map"
      :is-visible="isPopupVisible"
      :coordinates="popupCoordinates"
      :feature="popupFeature"
      :counter="popupCounter"
      :settings="settings"
      :code="props.code"
      @close="closePopup"
    />
  </MapSource>
</template>

<style scoped>
.feature-popup {
  padding: 8px;
}

.feature-popup h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
}

.feature-popup h4 {
  margin-top: 12px;
  margin-bottom: 8px;
  font-size: 14px;
}

.properties, .attributes {
  max-height: 300px;
  overflow-y: auto;
}

.property, .attribute {
  margin-bottom: 5px;
}

.no-properties {
  font-style: italic;
  color: #666;
}

.loading-details {
  font-style: italic;
  color: #666;
  margin: 10px 0;
}

.detailed-properties {
  margin-top: 5px;
}
</style>
